// src/pages/NotFound.js
import SearchIcon from "@mui/icons-material/Search"; // <-- Añadir icono de búsqueda
import {
  Box,
  Button,
  Container,
  Fade,
  IconButton, // <-- Añadir IconButton
  InputAdornment, // <-- Añadir InputAdornment
  Stack, // <-- Añadir Stack para organizar botones
  TextField, // <-- Añadir TextField para búsqueda
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { logEvent } from "firebase/analytics";
import React, { useEffect, useMemo, useState } from "react"; // <-- Añadir useState
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom"; // <-- Añadir useNavigate
import ScreenReaderOnly from "../components/accessibility/ScreenReaderOnly";
import { analytics } from "../services/firebase";
import { safeTranslate } from "../utils/languageUtils";
// Eliminar esta línea:

/**
 * Página de error 404 - No encontrado
 * Implementa accesibilidad avanzada y SEO optimizado para esta ruta de error
 */
const NotFound = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate(); // <-- Añadir hook de navegación
  const [searchTerm, setSearchTerm] = useState(""); // <-- Estado para la búsqueda

  // Datos estructurados para SEO
  const structuredData = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: safeTranslate("notFound.title", "Página no encontrada", {}, t),
      description: safeTranslate(
        "notFound.description",
        "Lo sentimos, la página que buscas no existe.",
        {},
        t,
      ),
      publisher: {
        "@type": "Organization",
        name: "VicDown",
        logo: {
          "@type": "ImageObject",
          url: `${window.location.origin}/assets/logo.png`,
        },
      },
    };
  }, [t]);

  // Ruta para el seguimiento analítico
  const notFoundPath = useMemo(() => location.pathname, [location.pathname]);

  // Seguimiento de errores 404 para mejorar la experiencia de usuario
  useEffect(() => {
    try {
      document.title = `${safeTranslate(
        "notFound.pageTitle",
        "Página no encontrada",
        {},
        t,
      )} | VicDown`;

      // Registrar evento 404 para analíticas
      if (analytics) {
        logEvent(analytics, "page_404", {
          page_path: notFoundPath,
          page_title: "Not Found",
          timestamp: new Date().toISOString(),
        });
      }

      // Insertar schema.org para SEO
      let scriptSchema = document.querySelector("#schema-not-found");
      if (!scriptSchema) {
        scriptSchema = document.createElement("script");
        scriptSchema.id = "schema-not-found";
        scriptSchema.type = "application/ld+json";
        document.head.appendChild(scriptSchema);
      }
      scriptSchema.textContent = JSON.stringify(structuredData);

      return () => {
        // Limpiar título y schema al desmontar
        if (scriptSchema && scriptSchema.parentNode) {
          scriptSchema.parentNode.removeChild(scriptSchema);
        }
      };
    } catch (error) {
      console.error("Error en NotFound tracking:", error);
      // Manejar silenciosamente para no interrumpir la UI
    }
  }, [t, notFoundPath, structuredData]);

  // URL de imagen con fallback
  const notFoundImage = useMemo(() => {
    return "/assets/images/404-illustration.png";
  }, []);

  // Manejador para la búsqueda
  const handleSearch = (event) => {
    event.preventDefault();
    if (searchTerm.trim()) {
      // Navegar a una página de resultados de búsqueda (ajusta la ruta si es necesario)
      navigate(`/search?query=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  return (
    <>
      {/* SEO Mejorado */}
      <Helmet>
        <title>
          {safeTranslate("notFound.pageTitle", "Página no encontrada", {}, t)} |
          VicDown
        </title>
        <meta
          name="description"
          content={safeTranslate(
            "notFound.metaDescription",
            "Lo sentimos, la página que buscas no existe. Vuelve a la página de inicio para continuar navegando.",
            {},
            t,
          )}
        />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={`${window.location.origin}/404`} />
        <meta
          property="og:title"
          content={`${safeTranslate(
            "notFound.pageTitle",
            "Página no encontrada",
            {},
            t,
          )} | VicDown`}
        />
        <meta
          property="og:description"
          content={safeTranslate(
            "notFound.metaDescription",
            "Lo sentimos, la página que buscas no existe. Vuelve a la página de inicio para continuar navegando.",
            {},
            t,
          )}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/404`} />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      {/* Anuncios para lectores de pantalla */}
      <ScreenReaderOnly>
        {safeTranslate(
          "notFound.screenReaderInfo",
          "Error 404. La página que buscas no se ha encontrado. Puedes usar el botón para volver a la página de inicio.",
          {},
          t,
        )}
      </ScreenReaderOnly>

      <Container
        maxWidth="md"
        sx={{
          py: 6,
          px: 2,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: "calc(100vh - 120px)", // Ajustar altura mínima si es necesario para centrar verticalmente sin flexGrow
        }}
        component="main"
        id="main-content"
      >
        <Fade in timeout={1000}>
          <Box component="section">
            <Box
              component="img"
              src={notFoundImage}
              alt={safeTranslate(
                "notFound.imageAlt",
                "Ilustración de página no encontrada",
                {},
                t,
              )}
              sx={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: "250px",
                mb: 4,
                opacity: 0.8,
                filter: "drop-shadow(0px 4px 8px rgba(0,0,0,0.2))",
              }}
              onError={(e) => {
                // Fallback en caso de error al cargar la imagen
                e.target.src = "/assets/logo.png";
                e.target.alt = safeTranslate(
                  "header.logoAlt",
                  "Logo de VicDown",
                  {},
                  t,
                );
              }}
            />

            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "3rem", md: "5rem" },
                fontWeight: 700,
                color: theme.palette.error.main,
                textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              {safeTranslate("notFound.title", "Página no encontrada", {}, t)}
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 4,
                fontSize: { xs: "1.5rem", md: "2rem" },
                color: theme.palette.text.secondary,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              {safeTranslate(
                "notFound.description",
                "Lo sentimos, la página que buscas no existe.",
                {},
                t,
              )}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: theme.palette.text.secondary,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              {safeTranslate(
                "notFound.suggestion",
                "La URL solicitada no se encontró en este servidor. Comprueba que la dirección sea correcta o vuelve a la página de inicio.",
                {},
                t,
              )}
            </Typography>

            {/* --- MEJORA: Barra de Búsqueda --- */}
            <Box
              component="form"
              onSubmit={handleSearch}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxWidth: "400px",
                mx: "auto",
                mb: 4, // Añadir margen inferior
              }}
              role="search"
            >
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={safeTranslate(
                  "notFound.searchPlaceholder",
                  "¿Qué estás buscando?",
                  {},
                  t,
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label={safeTranslate(
                  "notFound.searchAriaLabel",
                  "Buscar en el sitio",
                  {},
                  t,
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="submit"
                        aria-label={safeTranslate(
                          "notFound.searchSubmitAriaLabel",
                          "Realizar búsqueda",
                          {},
                          t,
                        )}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {/* --- FIN MEJORA --- */}

            {/* --- MEJORA: Enlaces Populares --- */}
            <Typography
              variant="body2"
              sx={{ mb: 1, color: theme.palette.text.secondary }}
            >
              {safeTranslate(
                "notFound.popularLinksIntro",
                "O quizás querías ir a:",
                {},
                t,
              )}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              <Button component={RouterLink} to="/" size="small">
                {safeTranslate("notFound.linkHome", "Inicio", {}, t)}
              </Button>
              <Button component={RouterLink} to="/blog" size="small">
                {safeTranslate("notFound.linkBlog", "Blog", {}, t)}
              </Button>
              <Button component={RouterLink} to="/forum" size="small">
                {safeTranslate("notFound.linkForum", "Foro", {}, t)}
              </Button>
              <Button component={RouterLink} to="/info" size="small">
                {safeTranslate("notFound.linkInfo", "Recursos", {}, t)}
              </Button>
            </Stack>
            {/* --- FIN MEJORA --- */}

            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.2rem",
                borderRadius: "25px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05) translateY(-3px)",
                  boxShadow: "0 6px 15px rgba(0,0,0,0.25)",
                },
              }}
              aria-label={safeTranslate(
                "notFound.buttonAriaLabel",
                "Volver a la página de inicio",
                {},
                t,
              )}
            >
              {safeTranslate("notFound.button", "Volver al inicio", {}, t)}
            </Button>

            {/* Sección adicional para usuarios con síndrome de Down */}
            <Box
              sx={{
                mt: 6,
                p: 3,
                borderRadius: "10px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.03)",
              }}
              role="complementary"
              aria-label={safeTranslate(
                "notFound.helpSectionLabel",
                "Ayuda adicional",
                {},
                t,
              )}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.5rem",
                  mb: 2,
                  color: theme.palette.primary.main,
                }}
              >
                {safeTranslate("notFound.needHelp", "¿Necesitas ayuda?", {}, t)}
              </Typography>

              <Typography variant="body1" paragraph>
                {safeTranslate(
                  "notFound.contactSupport",
                  "Si tienes problemas para encontrar lo que buscas, puedes:",
                  {},
                  t,
                )}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/contact"
                  aria-label={safeTranslate(
                    "notFound.contactButtonAriaLabel",
                    "Ir a la página de contacto",
                    {},
                    t,
                  )}
                >
                  {safeTranslate(
                    "notFound.contactButton",
                    "Contactar con nosotros",
                    {},
                    t,
                  )}
                </Button>

                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/sitemap"
                  aria-label={safeTranslate(
                    "notFound.sitemapButtonAriaLabel",
                    "Ver mapa del sitio",
                    {},
                    t,
                  )}
                >
                  {safeTranslate(
                    "notFound.sitemapButton",
                    "Ver mapa del sitio",
                    {},
                    t,
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Container>
    </>
  );
};

export default NotFound;
